/**
 * Complete Production-Grade Promotional Banners Route — Module 10.
 * Enhanced with Action Dropdowns, Live Previews, Filtering Menu Tabs, and Custom Delete Modals.
 */

import { createFileRoute } from "@tanstack/react-router";
import { useState, useEffect, useMemo, useRef } from "react";
import { siteConfig } from "@/config/site";
import {
  Image as ImageIcon,
  Plus,
  Edit,
  Trash2,
  Check,
  Eye,
  MoreVertical,
  Copy,
  ArrowUp,
  ArrowDown,
  Power,
  Search,
  SlidersHorizontal,
  Sparkles,
  AlertTriangle,
  X,
  ExternalLink,
  Smartphone,
  Monitor,
} from "lucide-react";
import {
  fetchAdminBannersFromSupabase,
  getStoredBanners,
  saveStoredBanners,
  syncBannerToSupabase,
  deleteBannerFromSupabase,
  type HeroBannerRecord,
} from "@/features/admin/cms/store/admin-cms-store";
import { BannerFormModal } from "@/components/admin/banners/banner-form-modal";
import { useLockBodyScroll } from "@/hooks/use-lock-body-scroll";

export const Route = createFileRoute("/admin/banners")({
  head: () => ({
    meta: [{ title: `Banner Management — Admin — ${siteConfig.name}` }],
  }),
  component: AdminBannersPage,
});

type PlacementFilter =
  "ALL" | "MID_PAGE_STRIP" | "HERO_MAIN" | "CATEGORY_HEADER" | "ACTIVE" | "INACTIVE";

function AdminBannersPage() {
  const [banners, setBanners] = useState<HeroBannerRecord[]>(() => getStoredBanners());
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState<PlacementFilter>("ALL");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingBanner, setEditingBanner] = useState<HeroBannerRecord | null>(null);
  const [previewBanner, setPreviewBanner] = useState<HeroBannerRecord | null>(null);
  const [previewDevice, setPreviewDevice] = useState<"desktop" | "mobile">("desktop");
  const [deletingBanner, setDeletingBanner] = useState<HeroBannerRecord | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null);
  const [toastMsg, setToastMsg] = useState<string | null>(null);

  useLockBodyScroll(!!deletingBanner || !!previewBanner);

  const loadData = () => {
    fetchAdminBannersFromSupabase().then((data) => {
      if (data && data.length > 0) {
        setBanners(data);
      }
    });
  };

  useEffect(() => {
    loadData();

    window.addEventListener("banners_updated", loadData);

    let bc: BroadcastChannel | null = null;
    try {
      bc = new BroadcastChannel("banners_sync");
      bc.onmessage = (e) => {
        if (e.data?.type === "BANNERS_UPDATED") {
          loadData();
        }
      };
    } catch {
      // Ignored
    }

    return () => {
      window.removeEventListener("banners_updated", loadData);
      if (bc) bc.close();
    };
  }, []);

  // Close open dropdowns on outside click
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest("[data-menu-container]")) {
        setActiveMenuId(null);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const triggerToast = (msg: string) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(null), 3000);
  };

  const updateStore = (list: HeroBannerRecord[]) => {
    setBanners(list);
    saveStoredBanners(list);
  };

  const handleOpenAdd = () => {
    setEditingBanner(null);
    setModalOpen(true);
    setActiveMenuId(null);
  };

  const handleOpenEdit = (b: HeroBannerRecord) => {
    setEditingBanner(b);
    setModalOpen(true);
    setActiveMenuId(null);
  };

  const handleToggleStatus = async (id: string) => {
    setActiveMenuId(null);
    const updated = banners.map((b) =>
      b.id === id
        ? {
            ...b,
            status: (b.status === "ACTIVE" ? "INACTIVE" : "ACTIVE") as HeroBannerRecord["status"],
          }
        : b,
    );
    updateStore(updated);
    const target = updated.find((b) => b.id === id);
    if (target) await syncBannerToSupabase(target);
    triggerToast("Banner status updated.");
  };

  const handleDuplicate = async (b: HeroBannerRecord) => {
    setActiveMenuId(null);
    const duplicated: HeroBannerRecord = {
      ...b,
      id: `ban-${Date.now()}`,
      title: `${b.title} (Copy)`,
      displayOrder: banners.length + 1,
    };
    const updated = [duplicated, ...banners];
    updateStore(updated);
    await syncBannerToSupabase(duplicated);
    triggerToast(`Banner "${duplicated.title}" duplicated.`);
  };

  const handleMovePriority = async (id: string, direction: "up" | "down") => {
    setActiveMenuId(null);
    const idx = banners.findIndex((b) => b.id === id);
    if (idx === -1) return;
    if (direction === "up" && idx === 0) return;
    if (direction === "down" && idx === banners.length - 1) return;

    const targetIdx = direction === "up" ? idx - 1 : idx + 1;
    const newBanners = [...banners];
    const temp = newBanners[idx];
    newBanners[idx] = newBanners[targetIdx];
    newBanners[targetIdx] = temp;

    // Recalculate displayOrder
    const reordered = newBanners.map((b, i) => ({ ...b, displayOrder: i + 1 }));
    updateStore(reordered);
    await Promise.all(reordered.map((b) => syncBannerToSupabase(b)));
    triggerToast("Banner priority updated.");
  };

  const handleConfirmDelete = async () => {
    if (!deletingBanner) return;
    setIsDeleting(true);
    const prevBanners = [...banners];
    try {
      const updated = banners.filter((b) => b.id !== deletingBanner.id);
      updateStore(updated);
      await deleteBannerFromSupabase(deletingBanner.id);
      triggerToast(`Banner "${deletingBanner.title}" deleted.`);
    } catch (err: unknown) {
      console.error("Delete banner failed:", err);
      updateStore(prevBanners);
      triggerToast(
        `Failed to delete banner: ${(err as { message?: string }).message || "Database error"}`,
      );
    } finally {
      setIsDeleting(false);
      setDeletingBanner(null);
    }
  };

  const handleSaveBanner = async (formData: Partial<HeroBannerRecord>) => {
    const prevBanners = [...banners];
    try {
      if (editingBanner) {
        const updated = banners.map((b) =>
          b.id === editingBanner.id ? ({ ...b, ...formData } as HeroBannerRecord) : b,
        );
        updateStore(updated);
        await syncBannerToSupabase({ ...editingBanner, ...formData });
        triggerToast(`Banner "${formData.title}" updated successfully.`);
      } else {
        const newBanner = {
          id: `ban-${Date.now()}`,
          title: formData.title || "Promotional Banner",
          subtitle: formData.subtitle || "",
          ctaText: formData.ctaText || "Shop Now",
          ctaLink: formData.ctaLink || "/shop",
          image: formData.image || "/banners/banner-living-room.webp",
          placement: formData.placement || "MID_PAGE_STRIP",
          status: formData.status || "ACTIVE",
          displayOrder: banners.length + 1,
        } as HeroBannerRecord;
        const updated = [newBanner, ...banners];
        updateStore(updated);
        await syncBannerToSupabase(newBanner);
        triggerToast(`Banner "${newBanner.title}" created successfully.`);
      }
    } catch (err: unknown) {
      console.error("Save banner failed:", err);
      updateStore(prevBanners);
      triggerToast(
        `Failed to save banner in database: ${(err as { message?: string }).message || "Database error"}`,
      );
    }
  };

  // Filter & Search Logic
  const filteredBanners = useMemo(() => {
    return banners.filter((b) => {
      // Search matching
      const matchesSearch =
        searchQuery.trim() === "" ||
        b.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (b.subtitle && b.subtitle.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (b.ctaText && b.ctaText.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (b.ctaLink && b.ctaLink.toLowerCase().includes(searchQuery.toLowerCase()));

      if (!matchesSearch) return false;

      // Tab filtering
      if (activeFilter === "ALL") return true;
      if (activeFilter === "ACTIVE") return b.status === "ACTIVE";
      if (activeFilter === "INACTIVE") return b.status === "INACTIVE";
      return b.placement === activeFilter;
    });
  }, [banners, searchQuery, activeFilter]);

  const activeCount = useMemo(() => banners.filter((b) => b.status === "ACTIVE").length, [banners]);
  const inactiveCount = useMemo(
    () => banners.filter((b) => b.status === "INACTIVE").length,
    [banners],
  );

  return (
    <div className="space-y-6 pb-16">
      {toastMsg && (
        <div className="fixed top-20 right-6 z-50 bg-foreground text-background text-xs font-semibold px-4 py-3 rounded-md shadow-xl flex items-center gap-2 animate-in fade-in duration-150">
          <Check className="h-4 w-4 text-emerald-400" />
          <span>{toastMsg}</span>
        </div>
      )}

      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-border pb-6">
        <div>
          <p className="eyebrow text-xs text-primary font-semibold">
            Storefront Media Architecture
          </p>
          <h1 className="mt-1 font-display text-3xl text-foreground">Promotional Banners</h1>
          <p className="mt-1 text-xs text-muted-foreground">
            Manage homepage hero carousel slides, category header banners, and mid-page promotional
            strips.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={handleOpenAdd}
            className="inline-flex min-h-10 items-center gap-2 bg-primary px-4 text-xs font-semibold uppercase tracking-wider text-primary-foreground rounded hover:opacity-90 transition-opacity shadow-xs"
          >
            <Plus className="h-4 w-4" /> Create Banner
          </button>
        </div>
      </div>

      {/* Controls: Search Bar & Navigation Filter Tabs */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        {/* Placement Filter Tabs Menu */}
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 sm:pb-0 scrollbar-none">
          <button
            onClick={() => setActiveFilter("ALL")}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-[color,background-color,border-color,box-shadow,transform,opacity] shrink-0 ${
              activeFilter === "ALL"
                ? "bg-primary text-primary-foreground font-semibold shadow-xs"
                : "bg-secondary/50 text-muted-foreground hover:text-foreground hover:bg-secondary"
            }`}
          >
            All Banners ({banners.length})
          </button>
          <button
            onClick={() => setActiveFilter("ACTIVE")}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-[color,background-color,border-color,box-shadow,transform,opacity] shrink-0 flex items-center gap-1.5 ${
              activeFilter === "ACTIVE"
                ? "bg-emerald-700 text-white font-semibold shadow-xs"
                : "bg-secondary/50 text-muted-foreground hover:text-foreground hover:bg-secondary"
            }`}
          >
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
            Active ({activeCount})
          </button>
          <button
            onClick={() => setActiveFilter("INACTIVE")}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-[color,background-color,border-color,box-shadow,transform,opacity] shrink-0 ${
              activeFilter === "INACTIVE"
                ? "bg-stone-700 text-white font-semibold shadow-xs"
                : "bg-secondary/50 text-muted-foreground hover:text-foreground hover:bg-secondary"
            }`}
          >
            Inactive ({inactiveCount})
          </button>
          <button
            onClick={() => setActiveFilter("MID_PAGE_STRIP")}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-[color,background-color,border-color,box-shadow,transform,opacity] shrink-0 ${
              activeFilter === "MID_PAGE_STRIP"
                ? "bg-primary text-primary-foreground font-semibold shadow-xs"
                : "bg-secondary/50 text-muted-foreground hover:text-foreground hover:bg-secondary"
            }`}
          >
            Mid-Page Strips
          </button>
          <button
            onClick={() => setActiveFilter("HERO_MAIN")}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-[color,background-color,border-color,box-shadow,transform,opacity] shrink-0 ${
              activeFilter === "HERO_MAIN"
                ? "bg-primary text-primary-foreground font-semibold shadow-xs"
                : "bg-secondary/50 text-muted-foreground hover:text-foreground hover:bg-secondary"
            }`}
          >
            Hero Carousel
          </button>
          <button
            onClick={() => setActiveFilter("CATEGORY_HEADER")}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-[color,background-color,border-color,box-shadow,transform,opacity] shrink-0 ${
              activeFilter === "CATEGORY_HEADER"
                ? "bg-primary text-primary-foreground font-semibold shadow-xs"
                : "bg-secondary/50 text-muted-foreground hover:text-foreground hover:bg-secondary"
            }`}
          >
            Category Headers
          </button>
        </div>

        {/* Live Search Input */}
        <div className="relative w-full md:w-72 shrink-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search banners by title, CTA..."
            className="w-full h-9 pl-9 pr-8 bg-card border border-border rounded-lg text-xs text-foreground placeholder:text-muted-foreground outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-[color,background-color,border-color,box-shadow,transform,opacity]"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Empty State */}
      {filteredBanners.length === 0 && (
        <div className="border border-dashed border-border rounded-xl p-12 text-center bg-card/50 space-y-3">
          <ImageIcon className="mx-auto h-10 w-10 text-muted-foreground opacity-60" />
          <h3 className="font-display font-semibold text-foreground text-base">No Banners Found</h3>
          <p className="text-xs text-muted-foreground max-w-sm mx-auto">
            {searchQuery
              ? `No banners match "${searchQuery}". Try a different search query.`
              : "No promotional banners configured for this filter. Create your first banner to display in storefront."}
          </p>
          <button
            onClick={handleOpenAdd}
            className="mt-2 inline-flex items-center gap-1.5 px-4 py-2 bg-primary text-primary-foreground rounded text-xs font-semibold uppercase tracking-wider hover:opacity-90"
          >
            <Plus className="h-3.5 w-3.5" /> Create Banner
          </button>
        </div>
      )}

      {/* Banner Grid */}
      <div className="grid gap-6 sm:grid-cols-2">
        {filteredBanners.map((b, idx) => (
          <div
            key={b.id}
            className="bg-card border border-border rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow space-y-3 p-4 flex flex-col justify-between relative"
          >
            <div className="space-y-3">
              {/* Media Thumbnail with Badges & Top Actions */}
              <div className="relative h-48 rounded-lg overflow-hidden border border-border bg-secondary group">
                <img src={b.image} alt={b.title} className="h-full w-full object-cover" />

                {/* Placement Badge */}
                <span className="absolute top-2.5 left-2.5 bg-background/90 text-foreground font-mono text-[10px] font-bold px-2.5 py-1 rounded-md backdrop-blur-xs shadow-xs">
                  {b.placement}
                </span>

                {/* Status Badge */}
                <span
                  className={`absolute top-2.5 right-2.5 text-[10px] font-bold px-2.5 py-1 rounded-md shadow-xs ${
                    b.status === "ACTIVE"
                      ? "bg-emerald-500/90 text-white backdrop-blur-xs"
                      : "bg-stone-900/80 text-stone-300 backdrop-blur-xs border border-white/10"
                  }`}
                >
                  {b.status}
                </span>

                {/* Hover Quick Preview Button */}
                <button
                  onClick={() => setPreviewBanner(b)}
                  className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2 text-white font-medium text-xs backdrop-blur-[2px]"
                >
                  <Eye className="h-4 w-4" /> Live Preview
                </button>
              </div>

              {/* Banner Details */}
              <div>
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-display font-semibold text-base text-foreground line-clamp-1">
                    {b.title}
                  </h3>
                </div>
                <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                  {b.subtitle || "No subtitle provided."}
                </p>
                <div className="mt-2.5 flex items-center justify-between text-xs font-mono bg-secondary/30 px-2.5 py-1.5 rounded-md border border-border/50">
                  <span className="text-primary font-semibold truncate max-w-[180px]">
                    CTA: {b.ctaText} → {b.ctaLink}
                  </span>
                  <span className="text-muted-foreground shrink-0 font-medium">
                    Priority #{b.displayOrder}
                  </span>
                </div>
              </div>
            </div>

            {/* Bottom Actions Bar with Direct Actions and 3-Dots Menu */}
            <div className="pt-3 border-t border-border flex items-center justify-between text-xs">
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => handleToggleStatus(b.id)}
                  className={`px-3 py-1.5 rounded-lg border font-medium transition-colors flex items-center gap-1.5 ${
                    b.status === "ACTIVE"
                      ? "border-border hover:bg-secondary text-foreground"
                      : "border-emerald-600 bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300"
                  }`}
                >
                  <Power className="h-3 w-3" />
                  {b.status === "ACTIVE" ? "Deactivate" : "Activate"}
                </button>
                <button
                  onClick={() => handleOpenEdit(b)}
                  className="px-3 py-1.5 border border-border rounded-lg bg-secondary/40 hover:bg-secondary font-semibold flex items-center gap-1 text-foreground transition-colors"
                >
                  <Edit className="h-3.5 w-3.5" /> Edit
                </button>
              </div>

              {/* 3-Dots Dropdown Menu Container */}
              <div className="relative" data-menu-container>
                <button
                  onClick={() => setActiveMenuId(activeMenuId === b.id ? null : b.id)}
                  className="p-1.5 rounded-lg border border-border hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
                  aria-label="More banner actions"
                >
                  <MoreVertical className="h-4 w-4" />
                </button>

                {/* Dropdown Menu Popup */}
                {activeMenuId === b.id && (
                  <div className="absolute right-0 bottom-full mb-1.5 w-48 bg-card border border-border rounded-xl shadow-xl py-1.5 z-40 animate-in fade-in zoom-in-95 duration-100">
                    <button
                      onClick={() => {
                        setPreviewBanner(b);
                        setActiveMenuId(null);
                      }}
                      className="w-full flex items-center gap-2 px-3.5 py-2 text-xs font-medium text-foreground hover:bg-secondary transition-colors"
                    >
                      <Eye className="h-3.5 w-3.5 text-primary" /> Live Preview
                    </button>
                    <button
                      onClick={() => handleDuplicate(b)}
                      className="w-full flex items-center gap-2 px-3.5 py-2 text-xs font-medium text-foreground hover:bg-secondary transition-colors"
                    >
                      <Copy className="h-3.5 w-3.5 text-primary" /> Duplicate Banner
                    </button>
                    <button
                      onClick={() => handleMovePriority(b.id, "up")}
                      disabled={idx === 0}
                      className="w-full flex items-center gap-2 px-3.5 py-2 text-xs font-medium text-foreground hover:bg-secondary transition-colors disabled:opacity-40"
                    >
                      <ArrowUp className="h-3.5 w-3.5 text-muted-foreground" /> Move Up in Order
                    </button>
                    <button
                      onClick={() => handleMovePriority(b.id, "down")}
                      disabled={idx === filteredBanners.length - 1}
                      className="w-full flex items-center gap-2 px-3.5 py-2 text-xs font-medium text-foreground hover:bg-secondary transition-colors disabled:opacity-40"
                    >
                      <ArrowDown className="h-3.5 w-3.5 text-muted-foreground" /> Move Down in Order
                    </button>
                    <div className="my-1 border-t border-border" />
                    <button
                      onClick={() => {
                        setDeletingBanner(b);
                        setActiveMenuId(null);
                      }}
                      className="w-full flex items-center gap-2 px-3.5 py-2 text-xs font-semibold text-destructive hover:bg-destructive/10 transition-colors"
                    >
                      <Trash2 className="h-3.5 w-3.5" /> Delete Banner
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Create / Edit Banner Form Modal */}
      <BannerFormModal
        open={modalOpen}
        banner={editingBanner}
        onClose={() => setModalOpen(false)}
        onSave={handleSaveBanner}
      />

      {/* Interactive Storefront Live Preview Modal */}
      {previewBanner && (
        <div className="fixed inset-0 z-50 bg-black/75 backdrop-blur-xs flex items-center justify-center p-4 sm:p-6 animate-in fade-in duration-150">
          <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-4xl overflow-hidden flex flex-col max-h-[90vh] animate-in zoom-in-95 duration-150">
            {/* Preview Header */}
            <div className="px-6 py-4 border-b border-border flex items-center justify-between bg-secondary/30">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                  <Eye className="h-4.5 w-4.5" />
                </div>
                <div>
                  <h3 className="font-display font-bold text-foreground text-lg">
                    Storefront Live Preview
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    Previewing banner as it appears to customers
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {/* Device Switcher */}
                <div className="flex items-center bg-secondary rounded-lg p-0.5 border border-border">
                  <button
                    onClick={() => setPreviewDevice("desktop")}
                    className={`p-1.5 rounded-md text-xs font-medium flex items-center gap-1 transition-colors ${
                      previewDevice === "desktop"
                        ? "bg-card text-foreground shadow-xs"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                    title="Desktop Preview"
                  >
                    <Monitor className="h-3.5 w-3.5" /> Desktop
                  </button>
                  <button
                    onClick={() => setPreviewDevice("mobile")}
                    className={`p-1.5 rounded-md text-xs font-medium flex items-center gap-1 transition-colors ${
                      previewDevice === "mobile"
                        ? "bg-card text-foreground shadow-xs"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                    title="Mobile Preview"
                  >
                    <Smartphone className="h-3.5 w-3.5" /> Mobile
                  </button>
                </div>

                <button
                  onClick={() => setPreviewBanner(null)}
                  className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Preview Body Canvas */}
            <div className="p-6 overflow-y-auto bg-stone-950/90 flex items-center justify-center min-h-[340px]">
              <div
                className={`w-full transition-[color,background-color,border-color,box-shadow,transform,opacity] duration-300 ${
                  previewDevice === "mobile"
                    ? "max-w-xs border border-white/10 rounded-2xl p-2 bg-black shadow-2xl"
                    : "max-w-3xl"
                }`}
              >
                <div className="group relative block overflow-hidden rounded-2xl border border-emerald-500/30 shadow-2xl">
                  <div
                    className={`relative w-full overflow-hidden bg-stone-950 ${
                      previewDevice === "mobile" ? "aspect-[4/3]" : "aspect-[21/8]"
                    }`}
                  >
                    <img
                      src={previewBanner.image}
                      alt={previewBanner.title}
                      className="h-full w-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
                    />

                    {/* Gradient Overlay & Content */}
                    <div className="absolute inset-0 bg-gradient-to-t from-stone-950/90 via-stone-950/30 to-transparent flex items-end justify-between p-4 sm:p-6">
                      <div className="flex flex-col gap-1 max-w-md text-left">
                        {previewBanner.subtitle && (
                          <div className="inline-flex items-center gap-1.5 bg-stone-950/90 backdrop-blur-md px-2.5 py-1 rounded-full border border-amber-400/40 text-amber-300 text-[10px] font-bold shadow-lg w-fit">
                            <Sparkles className="h-3 w-3 text-amber-400 animate-pulse" />
                            <span className="uppercase tracking-wider font-bold truncate max-w-[240px]">
                              {previewBanner.subtitle}
                            </span>
                          </div>
                        )}
                        <h4 className="font-display font-bold text-sm sm:text-xl text-white drop-shadow-md">
                          {previewBanner.title}
                        </h4>
                      </div>

                      <div className="ml-auto flex items-center gap-1.5 bg-gradient-to-r from-amber-400 to-amber-500 text-stone-950 px-4 py-2 rounded-xl font-bold text-[11px] uppercase tracking-wider shadow-lg shrink-0">
                        <span>{previewBanner.ctaText || "Shop Now"}</span>
                        <ExternalLink className="h-3.5 w-3.5" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Preview Footer */}
            <div className="px-6 py-3 border-t border-border flex items-center justify-between text-xs text-muted-foreground bg-card">
              <span className="font-mono">
                Placement: {previewBanner.placement} | Priority: #{previewBanner.displayOrder}
              </span>
              <button
                onClick={() => {
                  const b = previewBanner;
                  setPreviewBanner(null);
                  handleOpenEdit(b);
                }}
                className="inline-flex items-center gap-1.5 text-primary hover:underline font-semibold"
              >
                <Edit className="h-3.5 w-3.5" /> Edit this banner
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Modern Delete Confirmation Modal (Replaces browser confirm) */}
      {deletingBanner && (
        <div className="fixed inset-0 z-50 bg-black/65 backdrop-blur-xs flex items-center justify-center p-4 animate-in fade-in duration-150">
          <div className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-md p-6 space-y-5 animate-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between border-b border-border pb-3">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-destructive/10 text-destructive shrink-0">
                  <AlertTriangle className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="font-display text-lg text-foreground font-bold">Delete Banner</h3>
                  <p className="text-xs text-muted-foreground">Action cannot be undone</p>
                </div>
              </div>
              <button
                onClick={() => setDeletingBanner(null)}
                className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <p className="text-xs text-muted-foreground leading-relaxed">
              Are you sure you want to delete{" "}
              <strong className="text-foreground font-semibold font-display">
                "{deletingBanner.title}"
              </strong>
              ? This promotional banner will be permanently removed from your storefront and
              database.
            </p>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                onClick={() => setDeletingBanner(null)}
                disabled={isDeleting}
                className="px-4 py-2 border border-border rounded-lg text-xs font-semibold uppercase tracking-wider text-foreground hover:bg-secondary transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={isDeleting}
                className="px-5 py-2 bg-destructive text-destructive-foreground rounded-lg text-xs font-semibold uppercase tracking-wider hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center gap-2"
              >
                <Trash2 className="h-3.5 w-3.5" />
                {isDeleting ? "Deleting..." : "Delete Banner"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
